/**
 * Live unified search: Hubwayz hotels + LDS flights (parallel).
 */

import { searchFlights } from "../../services/fhpService";
import {
  fetchHubwayzDestinations,
  searchHubwayzHotels,
} from "../../services/hubwayzHotelsApi";
import { dealsListFromSearchFlightsResponseBody } from "../../utils/flightParallelSearchHelpers";
import { adaptDealToFlightOffer } from "../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { mapHubwayzToFlightHotelCards } from "./mapHubwayzToFlightHotelCards";
import { hideSupplierBrandFromUserText } from "./hideSupplierBrandFromUserText";
import { summarizeOccupancy } from "./demoFixtures";

function nightsBetween(checkIn, checkOut) {
  try {
    const a = new Date(`${checkIn}T12:00:00`);
    const b = new Date(`${checkOut}T12:00:00`);
    const d = Math.round((b - a) / 86400000);
    return d > 0 ? d : null;
  } catch {
    return null;
  }
}

function toDdMmYyyy(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  return `${m[3]}/${m[2]}/${m[1]}`;
}

async function resolveHubwayzDestinationId(dest) {
  if (dest?.hubwayzLocationId) return dest.hubwayzLocationId;
  const country = dest?.countryCode;
  if (!country) return null;
  const res = await fetchHubwayzDestinations(country);
  const list = Array.isArray(res?.destinations) ? res.destinations : [];
  const needle = String(dest?.hubwayzNameHint || dest?.label || "")
    .trim()
    .toLowerCase();
  const hit = list.find((d) => {
    const type = String(d?.type || "city").toLowerCase();
    if (type !== "city") return false;
    return (
      String(d?.seoname || "").toLowerCase() === needle ||
      String(d?.name || "").toLowerCase() === needle
    );
  });
  return hit?.id ?? null;
}

function flightOfferSortPrice(offer) {
  const candidates = [
    offer?.pricePerPerson,
    offer?.priceClientPerPersonUsd,
    offer?.totalFare,
    offer?.rawTotalFare,
    offer?.priceUsd,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return Number.POSITIVE_INFINITY;
}

/** Cheapest LDS offer first (by per-person / total fare). */
export function sortFlightOffersCheapestFirst(flightOffers) {
  const list = Array.isArray(flightOffers) ? [...flightOffers] : [];
  list.sort((a, b) => flightOfferSortPrice(a) - flightOfferSortPrice(b));
  return list;
}

function mapOfferLegToCardLeg(leg, offer) {
  if (!leg) return null;
  // adaptDealToFlightOffer → cardLegRows shape
  if (leg.originCode || leg.departureTime || leg.flightNumber) {
    return {
      from: leg.originCode || leg.from || "",
      to: leg.destinationCode || leg.to || "",
      depart: leg.departureTime || leg.depart || "",
      arrive: leg.arrivalTime || leg.arrive || "",
      airline: leg.airlineName || offer?.airlineSummary || "",
      airlineCode: leg.airlineCode || offer?.cardMeta?.airlineCode || "",
      flightNo: leg.flightNumber || leg.flightNo || "",
      duration: leg.duration || "",
      stopsLabel: leg.stopLabelHe || null,
      seatsLeft: leg.seatsLeft,
    };
  }
  return {
    from: leg.depIata || leg.from || "",
    to: leg.arrIata || leg.to || "",
    depart: leg.depTime || leg.depart || "",
    arrive: leg.arrTime || leg.arrive || "",
    airline: leg.airlineName || leg.airline || offer?.airline?.name || "",
    airlineCode: leg.airlineCode || offer?.airline?.code || "",
    flightNo: leg.flightNo || leg.flightNumber || "",
    duration: leg.durationLabel || leg.duration || "",
    seatsLeft: leg.seatsLeft,
  };
}

/**
 * Agent SERP shows ONE combined net (hotel + cheapest flight + HR fee).
 * Line-item split lives only on adminPriceBreakdown (backoffice later).
 * HR commission is a stub (0) until product wires the fee source.
 */
function mergeUnifiedAgentPrice(card, cheapest, paxCount) {
  const pax = Math.max(1, Number(paxCount) || Number(card?.paxCount) || 2);
  const hotelTotalRaw = Number(card?.hotelNetTotal);
  const hotelTotal = Number.isFinite(hotelTotalRaw)
    ? hotelTotalRaw
    : Number(card?.agentNetPerPerson) * pax || 0;
  const hotelPerPerson = hotelTotal / pax;

  const flightPerPersonRaw = Number(
    cheapest?.pricePerPerson ?? cheapest?.priceClientPerPersonUsd
  );
  const flightTotalRaw = Number(cheapest?.totalFare ?? cheapest?.rawTotalFare);
  let flightPerPerson = 0;
  if (Number.isFinite(flightPerPersonRaw) && flightPerPersonRaw > 0) {
    flightPerPerson = flightPerPersonRaw;
  } else if (Number.isFinite(flightTotalRaw) && flightTotalRaw > 0) {
    flightPerPerson = flightTotalRaw / pax;
  }

  // Placeholder — replace when HR fee SSOT is available for this flow.
  const hrCommissionPerPerson = Number(card?.hrCommissionPerPerson) || 0;

  const agentNetPerPerson = hotelPerPerson + flightPerPerson + hrCommissionPerPerson;

  const hotelIls = Number(card?.agentNetPerPersonIls);
  const flightIls = Number(cheapest?.priceClientPerPersonIls);
  const hrIls = Number(card?.hrCommissionPerPersonIls) || 0;
  const ilsParts = [];
  if (Number.isFinite(hotelIls) && hotelIls > 0) ilsParts.push(hotelIls);
  if (Number.isFinite(flightIls) && flightIls > 0) ilsParts.push(flightIls);
  if (hrIls > 0) ilsParts.push(hrIls);
  const agentNetPerPersonIls =
    ilsParts.length > 0 ? ilsParts.reduce((a, b) => a + b, 0) : null;

  return {
    paxCount: pax,
    hotelNetTotal: hotelTotal,
    agentNetPerPerson,
    agentNetPerPersonIls,
    pricePerPerson: agentNetPerPerson,
    totalForTwo: agentNetPerPerson * pax,
    hrCommissionPerPerson,
    flightOfferId: cheapest?.id || null,
    flightPricePerPerson: flightPerPerson > 0 ? flightPerPerson : null,
    flightCurrency: cheapest?.rawCurrency || card?.currency || "USD",
    adminPriceBreakdown: {
      pax,
      currency: card?.currency || "USD",
      hotelTotal,
      hotelPerPerson,
      flightPerPerson,
      flightTotal: flightPerPerson * pax,
      hrCommissionPerPerson,
      hrCommissionTotal: hrCommissionPerPerson * pax,
      combinedPerPerson: agentNetPerPerson,
      combinedTotal: agentNetPerPerson * pax,
      flightOfferId: cheapest?.id || null,
    },
  };
}

function attachFlightStrip(hotelCards, flightOffers, paxCount = 2) {
  const sorted = sortFlightOffersCheapestFirst(flightOffers);
  const cheapest = sorted[0] || null;
  if (!cheapest) {
    return (hotelCards || []).map((card) => {
      const merged = mergeUnifiedAgentPrice(card, null, paxCount);
      return {
        ...card,
        ...merged,
        flightOptionsCount: 0,
      };
    });
  }

  const rows = Array.isArray(cheapest.cardLegRows) ? cheapest.cardLegRows : [];
  const out = cheapest.outbound || cheapest.legs?.[0] || rows[0] || null;
  const inn = cheapest.inbound || cheapest.legs?.[1] || rows[1] || null;
  const outbound = mapOfferLegToCardLeg(out, cheapest);
  const inbound = mapOfferLegToCardLeg(inn, cheapest);
  const direct =
    !!cheapest?.flags?.direct ||
    !!cheapest?.cardMeta?.isDirect ||
    (rows[0] ? rows[0].isDirect !== false : Number(out?.stops) === 0);

  const baggageChips = cheapest?.baggage?.chips;
  const baggageHint = cheapest?.baggageHint || cheapest?.cardBaggageHint;

  return hotelCards.map((card) => {
    const merged = mergeUnifiedAgentPrice(card, cheapest, paxCount);
    return {
      ...card,
      ...merged,
      directFlight: direct,
      flightOptionsCount: sorted.length,
      outbound,
      inbound,
      baggage: (() => {
        if (Array.isArray(baggageChips) && baggageChips.length) {
          const bullets = baggageChips
            .map((c) => c.label || c)
            .filter(Boolean)
            .slice(0, 3);
          const joined = bullets.join(" ").toLowerCase();
          const level = /טרולי|trolley/.test(joined)
            ? "trolley"
            : /מזוודה|checked|suitcase|luggage|כבודה/.test(joined)
              ? "checked"
              : "checked";
          return { none: false, level, bullets };
        }
        if (baggageHint) {
          const hint = String(baggageHint);
          const low = hint.toLowerCase();
          const level = /טרולי|trolley/.test(low)
            ? "trolley"
            : /ללא/.test(low)
              ? "none"
              : "checked";
          return {
            none: level === "none",
            level,
            bullets: level === "none" ? [] : [hint],
          };
        }
        return card.baggage;
      })(),
    };
  });
}

/**
 * @param {object} applied — unified appliedSearch
 * @param {object} destMeta — UNIFIED_DESTINATIONS entry
 */
export async function runUnifiedLiveSearch(applied, destMeta) {
  const occ = summarizeOccupancy(applied.rooms);
  const rooms = (applied.rooms || []).map((r) => ({
    adults: Math.max(1, Number(r.adults) || 1),
    childAges: Array.isArray(r.childAges) ? r.childAges : [],
  }));

  const nights = nightsBetween(applied.checkIn, applied.checkOut);
  const dateRangeLabel =
    applied.dateRangeLabel || `${applied.checkIn} → ${applied.checkOut}`;

  const destinationIdPromise = resolveHubwayzDestinationId(destMeta);

  const flightsPromise = (async () => {
    const origin = String(applied.origin || "TLV").toUpperCase();
    const destination = String(applied.flightIata || destMeta?.flightIata || "").toUpperCase();
    if (!destination || destination.length < 3) {
      return { offers: [], deals: [], error: null, skipped: true };
    }
    try {
      const kidsAges = rooms.flatMap((r) => r.childAges || []);
      const res = await searchFlights({
        dealType: applied.tripType === "oneway" ? "OneWay" : "RoundTrip",
        origin,
        destination,
        dep_date: toDdMmYyyy(applied.checkIn),
        ret_date:
          applied.tripType === "oneway" ? undefined : toDdMmYyyy(applied.checkOut),
        room_compositions: [
          {
            adults: occ.adults,
            kids: occ.children,
            infants: 0,
            kidsAges,
          },
        ],
      });
      const deals = dealsListFromSearchFlightsResponseBody(res?.data ?? res) || [];
      const roomInfo = {
        rooms: [
          {
            adults: occ.adults,
            kids: occ.children,
            infants: 0,
          },
        ],
      };
      const offers = sortFlightOffersCheapestFirst(
        deals
          .slice(0, 40)
          .map((deal, idx) => {
            try {
              return adaptDealToFlightOffer(deal, idx, roomInfo);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
      );
      return { offers, deals, error: null, skipped: false };
    } catch (err) {
      return {
        offers: [],
        deals: [],
        error: err?.message || String(err),
        skipped: false,
      };
    }
  })();

  const hotelsPromise = (async () => {
    try {
      const destinationId = await destinationIdPromise;
      if (destinationId == null) {
        return {
          cards: [],
          raw: null,
          error: "לא נמצא יעד ליעד שנבחר",
          destinationId: null,
        };
      }
      const res = await searchHubwayzHotels({
        destinationId,
        checkIn: applied.checkIn,
        checkOut: applied.checkOut,
        rooms,
        waitUntilDone: true,
        enrichStatic: true,
      });
      if (!res?.ok) {
        return {
          cards: [],
          raw: res,
          error: hideSupplierBrandFromUserText(res?.error, "החיפוש נכשל"),
          destinationId,
        };
      }
      const cards = mapHubwayzToFlightHotelCards(res.data || {}, {
        destinationId: destMeta?.id,
        dateRangeLabel,
        nights,
        cityLabel: destMeta?.label,
        countryLabel: destMeta?.countryLabel,
        searchRequest: res.requestPayload || null,
        paxCount: Math.max(1, occ.adults + occ.children),
      });
      return {
        cards,
        raw: res.data,
        searchRequest: res.requestPayload || null,
        error: null,
        destinationId,
      };
    } catch (err) {
      return {
        cards: [],
        raw: null,
        error: err?.message || String(err),
        destinationId: null,
      };
    }
  })();

  const [flights, hotels] = await Promise.all([flightsPromise, hotelsPromise]);
  const paxCount = Math.max(1, occ.adults + occ.children);
  const cardsWithFlights = attachFlightStrip(
    hotels.cards,
    flights.offers,
    paxCount
  );

  return {
    flightHotelCards: cardsWithFlights,
    flightOffers: flights.offers,
    hubwayzData: hotels.raw || null,
    searchRequest: hotels.searchRequest || null,
    hotelsError: hotels.error,
    flightsError: flights.error,
    hubwayzDestinationId: hotels.destinationId,
    meta: {
      nights,
      dateRangeLabel,
      hotelsCount: cardsWithFlights.length,
      flightsCount: flights.offers.length,
    },
  };
}
